import { useState, useCallback } from 'react';

/**
 * useNotifications — Native OS notifications via Web Notifications API.
 *
 * Subscribes to Supabase Realtime on the `notifications` table,
 * filtered by the current user's ID. When a new row is inserted,
 * fires a native browser notification.
 *
 * Usage:
 *   const { permission, requestPermission } = useNotifications(userId);
 */
export function useNotifications(userId) {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const sendNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted') return null;
    try {
      const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      return notification;
    } catch (err) {
      // Fallback: some browsers block Notification constructor
      console.warn('Notification failed:', err);
      return null;
    }
  }, [permission]);

  return { permission, requestPermission, sendNotification };
}
